import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/cn';
import {
  Activity, Download, Loader2, Check, AlertTriangle, X, Clock,
  HeartPulse, Zap,
} from 'lucide-react';

/**
 * DownloadVitalsECGButton — LifePak / ZOLL cloud transmission pull.
 *
 * The device-ingest edge function is the push path (monitor posts to
 * us). This button is the pull path — the crew opens their ePCR and
 * wants to grab any transmission their LifePak or ZOLL has pushed to
 * the vendor cloud but that we haven't yet received or linked.
 *
 * Flow: click → modal with the last 25 device transmissions for this
 * agency that aren't yet linked to an ePCR. Tap a row → links the
 * transmission to the current ePCR. The edge function does the rest:
 * inserts vital_sets, defib_events, cpr_quality_metrics, and ECG
 * attachments; mirrors shock + CPR quality into the Code 99 log if
 * a session is active.
 *
 * Until a vendor-specific live cloud-pull (Stryker CODE-STAT /
 * ZOLL Data Exchange) credential is provisioned, this component
 * surfaces the device_transmissions table we already have. That
 * gives the crew the ability to link any stray transmission to
 * their ePCR manually — solving the "monitor pushed to cloud but
 * no ePCR was open yet" race.
 */

interface DeviceTransmission {
  id: string;
  device_vendor: string;
  device_serial: string | null;
  transmission_id: string | null;
  transmission_received_at: string;
  epcr_id: string | null;
  incident_id: string | null;
  // Count of children, lazily populated
  vital_sets_count?: number;
  defib_events_count?: number;
}

export function DownloadVitalsECGButton({ incidentId, epcrId }: { incidentId: string; epcrId: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DeviceTransmission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  const [linkedAt, setLinkedAt] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      // Candidates: transmissions for this incident (device pushed without
      // an epcr_id) OR for another ePCR on the same incident, received in
      // the last 6 hours. Gives the crew a fast grab-list.
      const since = new Date(Date.now() - 6 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from('device_transmissions')
        .select('id, device_vendor, device_serial, transmission_id, transmission_received_at, epcr_id, incident_id')
        .or(`incident_id.eq.${incidentId},epcr_id.eq.${epcrId},epcr_id.is.null`)
        .gte('transmission_received_at', since)
        .order('transmission_received_at', { ascending: false })
        .limit(25);
      if (error) throw error;
      setRows((data as DeviceTransmission[] | null) ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function linkToEpcr(tx: DeviceTransmission) {
    setLinking(tx.id);
    setError(null);
    try {
      const { error } = await supabase
        .from('device_transmissions')
        .update({ epcr_id: epcrId, incident_id: incidentId })
        .eq('id', tx.id);
      if (error) throw error;
      // The ingest pipeline has already written children into the shared
      // tables keyed on transmission_id — those inherit the link because
      // they FK-cascade through device_transmissions.epcr_id on the
      // downstream queries in the ePCR tabs. For any children that
      // stored epcr_id directly, rewrite them here:
      await supabase.from('vital_sets').update({ epcr_id: epcrId }).eq('device_transmission_id', tx.id);
      await supabase.from('defib_events').update({ epcr_id: epcrId }).eq('device_transmission_id', tx.id);
      await supabase.from('cpr_quality_metrics').update({ epcr_id: epcrId }).eq('device_transmission_id', tx.id);
      setLinkedAt(tx.id);
      window.setTimeout(() => setOpen(false), 700);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLinking(null);
    }
  }

  return (
    <>
      <button
        onClick={() => { setOpen(true); void load(); }}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:border-red-600 rounded min-h-touch whitespace-nowrap"
        title="Pull vital signs + ECG + CPR data from LifePak / ZOLL cloud — can be re-run any time"
      >
        <HeartPulse size={14} className="text-red-400" />
        <span>Import Vitals/ECG</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3">
          <div className="w-full max-w-2xl max-h-[85vh] flex flex-col border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-700 bg-slate-950 px-3 py-1.5">
              <div className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-widest text-slate-200">
                <HeartPulse size={13} className="text-red-400" />
                Device Transmissions · LifePak / ZOLL
              </div>
              <button onClick={() => setOpen(false)} className="h-7 w-7 flex items-center justify-center text-slate-400 hover:bg-slate-800">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-10 text-slate-400 gap-2">
                  <Loader2 size={16} className="animate-spin" /> Loading transmissions…
                </div>
              ) : error ? (
                <div className="p-4 text-sm text-red-300 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5" /> {error}
                </div>
              ) : rows.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">
                  No recent device transmissions. Confirm the monitor is connected to the agency's LifePak Cloud / ZOLL Data Exchange, and that the receiving webhook <code className="font-mono">/functions/v1/device-ingest</code> is reachable.
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {rows.map((tx) => {
                    const busy = linking === tx.id;
                    const done = linkedAt === tx.id;
                    const alreadyLinked = tx.epcr_id === epcrId;
                    return (
                      <button
                        key={tx.id}
                        onClick={() => !alreadyLinked && void linkToEpcr(tx)}
                        disabled={busy || done || alreadyLinked}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-800 transition-colors',
                          done && 'bg-emerald-950/30',
                          alreadyLinked && 'opacity-60',
                        )}
                      >
                        <Zap size={14} className={cn(
                          'shrink-0',
                          tx.device_vendor === 'stryker_lifepak' ? 'text-amber-400' : 'text-blue-400',
                        )} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[11px] text-slate-500 tabular-nums">
                              <Clock size={10} className="inline mr-0.5" />
                              {new Date(tx.transmission_received_at).toLocaleString()}
                            </span>
                            <span className="status-pill bg-slate-800 text-slate-300 border border-slate-700 text-[9px]">
                              {tx.device_vendor.replace(/_/g, ' ')}
                            </span>
                            {tx.device_serial && (
                              <span className="font-mono text-[10px] text-slate-500">s/n {tx.device_serial}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {tx.transmission_id ? `Transmission ${tx.transmission_id.slice(0, 12)}…` : 'Unnamed transmission'}
                          </div>
                        </div>
                        <div className="shrink-0">
                          {busy          && <Loader2 size={14} className="animate-spin text-blue-400" />}
                          {done          && <Check size={14} className="text-emerald-400" />}
                          {alreadyLinked && !done && (
                            <span className="status-pill bg-emerald-950 text-emerald-300 border border-emerald-800 text-[9px]">
                              linked
                            </span>
                          )}
                          {!busy && !done && !alreadyLinked && <Download size={13} className="text-slate-400" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-slate-700 bg-slate-900 px-3 py-1.5 text-[10px] font-mono text-slate-500">
              Tap a transmission to pull its vital sets, defib events, CPR quality, and ECG attachments into this ePCR.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Keep unused Activity icon reserved for future expansion
void Activity;
